import React, { useState, useEffect } from 'react';
import { gql, useMutation } from '@apollo/client';

import { Button, LoadingEllipses } from '@trqk/components';
import { locale } from '@trqk/utils';

import {
    useEntities,
    useStatementSelection,
    useUploadStatements,
} from '../../../hooks';

const DELETE_TRQK_STATEMENT_VIEW = gql`
    mutation deleteTrqkStatementView($statement_id: ID!) {
        deleteTrqkStatementView(statement_id: $statement_id) {
            id
        }
    }
`;

/**
 * @typedef {ConfirmStatementDeletionInput}
 * @property {string[]} selectedStatementIds the array of statement IDs to delete
 * @property {string} userID current user ID
 */
interface ConfirmStatementDeletionInput {
    selectedStatementIds: string[];
    userId?: string;
}

/**
 * A RenderContent component for a Toast which shows a confirmation dialog to make sure the user
 * really wants to delete one or more statements.
 * @param props {ToastRenderContentProps<ConfirmStatementDeletionInput>}
 */
export const ConfirmStatementDeletion = ({
    remove,
    input,
}: ToastRenderContentProps<ConfirmStatementDeletionInput>): JSX.Element => {
    const selectedStatementIds = input?.selectedStatementIds ?? [];
    const userId = input?.userId ?? null;
    const [deletedStatementIds, setDeletedStatementIds] = useState<string[]>(
        [],
    );
    // If we use the loading property from the mutation when there is more than one element in
    // selectedStatementIds, it doesn't render correctly; we will see it flip to a non-loading state
    // after the first mutation completes. We're tracking `loading` with `useState()` instead.
    const [loading, setLoading] = useState(false);
    const statementOrStatements =
        selectedStatementIds.length === 1
            ? locale.enUS.MANAGE_STATEMENTS.TOASTS.CONFIRM_STATEMENT_DELETION
                  .STATEMENT
            : locale.enUS.MANAGE_STATEMENTS.TOASTS.CONFIRM_STATEMENT_DELETION
                  .STATEMENTS;
    const {
        statementSelectionProps: { sendStatementSelectionEvent },
    } = useStatementSelection();
    const { selectedEntityId, sendEntitySelectionEvent } = useEntities();

    const [deleteTrqkStatementView] = useMutation(DELETE_TRQK_STATEMENT_VIEW, {
        onCompleted: data => {
            const deletedStatementId = data?.deleteTrqkStatementView?.id;

            if (deletedStatementId) {
                setDeletedStatementIds(
                    (prevDeletedStatementIds: string[]): string[] =>
                        [deletedStatementId].concat(prevDeletedStatementIds),
                );
                // Keep statementSelectionManagerMachine up to date by deleting this statement from
                // its child contexts
                sendStatementSelectionEvent({
                    type: 'DELETE_STATEMENT',
                    value: {
                        // ID is only here for typechecking purposes. The DELETE_STATEMENT event is picked
                        // up by all child machines, so the statement will be deleted from every context.
                        id: '',
                        statementId: deletedStatementId,
                    },
                });

                // Remove statement from entity selection state. If this was the last statement
                // for an entity, the entity will be removed from the entity selector and the next
                // entity (alphabetically sorted by name) will be selected.
                sendEntitySelectionEvent({
                    type: 'DELETE_STATEMENT_FROM_ENTITIES',
                    value: {
                        statementId: deletedStatementId,
                    },
                });
            }
        },
        onError: () => {
            remove({
                canceled: false,
                success: false,
            });
        },
        update: (cache, result) => {
            const deletedStatementView =
                result?.data?.deleteTrqkStatementView ?? null;

            if (deletedStatementView !== null) {
                cache.evict({
                    id: cache.identify(deletedStatementView),
                });
            }
        },
    });

    useEffect(() => {
        if (deletedStatementIds.length === selectedStatementIds.length) {
            remove({
                canceled: false,
                success: true,
            });
        }
    }, [deletedStatementIds, selectedStatementIds, remove]);

    const { setEntityRefreshState } = useUploadStatements();

    return (
        <>
            <div className="font-semibold">
                {
                    locale.enUS.MANAGE_STATEMENTS.TOASTS
                        .CONFIRM_STATEMENT_DELETION.PART_1
                }
                &nbsp;{selectedStatementIds.length}&nbsp;
                {`${statementOrStatements}.`}
            </div>
            <div className="mt-6">
                {
                    locale.enUS.MANAGE_STATEMENTS.TOASTS
                        .CONFIRM_STATEMENT_DELETION.PART_2
                }
            </div>
            <div className="mt-8 flex justify-end">
                <Button
                    color="danger-tertiary-light-bg"
                    autoWidth={true}
                    addClasses="mr-4"
                    handleClick={(): void => {
                        remove({
                            canceled: true,
                            success: false,
                        });
                    }}
                >
                    <div className="text-center mx-3">
                        {
                            locale.enUS.MANAGE_STATEMENTS.TOASTS
                                .CONFIRM_STATEMENT_DELETION.BUTTONS.CANCEL
                        }
                    </div>
                </Button>
                <Button
                    color="danger-primary-light-bg"
                    autoWidth={true}
                    handleClick={(): void => {
                        selectedStatementIds.forEach(
                            (statementId: string): void => {
                                if (userId !== null) {
                                    setLoading(true);

                                    deleteTrqkStatementView({
                                        variables: {
                                            statement_id: statementId,
                                        },
                                    });
                                }
                            },
                        );

                        if (
                            selectedStatementIds.length > 0 &&
                            selectedEntityId !== null
                        ) {
                            // When a the user deletes a statement, set the corresponding
                            // entityRefreshState so that the Tracks table can detect that the
                            // tracks list is being rebuilt due to a statement deletion
                            setEntityRefreshState(
                                (
                                    prev: Record<string, EntityRefreshStatus>,
                                ): Record<string, EntityRefreshStatus> => {
                                    const prevEntityStatus =
                                        prev[selectedEntityId] ?? null;

                                    let nextEntityStatus: EntityRefreshStatus =
                                        {
                                            entityId: selectedEntityId,
                                            isIngestingStatement: false,
                                            isDeletingStatement: true,
                                            refreshedAt: 0,
                                            tracksFetchedAt: 0,
                                        };

                                    if (prevEntityStatus !== null) {
                                        // preserve prior values
                                        nextEntityStatus = {
                                            ...prevEntityStatus,
                                            isDeletingStatement: true,
                                        };
                                    }

                                    return {
                                        ...prev,
                                        [selectedEntityId]: nextEntityStatus,
                                    };
                                },
                            );
                        }
                    }}
                >
                    <div className="flex justify-center mx-3 w-32">
                        {!loading && (
                            <>
                                {
                                    locale.enUS.MANAGE_STATEMENTS.TOASTS
                                        .CONFIRM_STATEMENT_DELETION.BUTTONS
                                        .DELETE
                                }
                            </>
                        )}
                        {loading && (
                            <>
                                <LoadingEllipses width={40} />
                            </>
                        )}
                    </div>
                </Button>
            </div>
        </>
    );
};
